import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  Copy,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

interface FormFieldDef {
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[] | null;
  placeholder?: string | null;
}

interface CaptureFormDetail {
  id: string;
  campaignId: string;
  slug: string;
  publicToken: string;
  title: string;
  introText: string | null;
  thankYouText: string | null;
  fieldConfig: FormFieldDef[];
  status: string;
  embedEnabled: boolean;
  allowedOrigins: string;
  themeInheritHost: boolean;
  disabledMessage: string | null;
  schemaVersion: number;
}

const FIELD_TYPES = [
  'text',
  'email',
  'phone',
  'number',
  'textarea',
  'dropdown',
  'checkbox',
  'date',
] as const;

/**
 * Story 48.4b — admin edit page for one capture form.
 *
 * - Editable: title, intro text, thank-you, status, field list, default rep.
 * - Field list reorder via up/down arrows (sufficient for v1; dnd-kit
 *   later if real users complain).
 * - Token rotation button + public URL preview.
 */
export function LeadCaptureFormEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<CaptureFormDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Edit state
  const [title, setTitle] = useState('');
  const [introText, setIntroText] = useState('');
  const [thankYouText, setThankYouText] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [fields, setFields] = useState<FormFieldDef[]>([]);

  // Story 100.4 — embed state
  const [embedEnabled, setEmbedEnabled] = useState(false);
  const [allowedOrigins, setAllowedOrigins] = useState<string[]>([]);
  const [originDraft, setOriginDraft] = useState('');
  const [themeInheritHost, setThemeInheritHost] = useState(true);
  const [disabledMessage, setDisabledMessage] = useState('');
  const [snippetCopied, setSnippetCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiClient
      .get<CaptureFormDetail>(`/lead-capture-forms/${id}`)
      .then(({ data }) => {
        setForm(data);
        setTitle(data.title);
        setIntroText(data.introText ?? '');
        setThankYouText(data.thankYouText ?? '');
        setStatus(data.status);
        setFields(data.fieldConfig);
        setEmbedEnabled(data.embedEnabled);
        setAllowedOrigins(
          data.allowedOrigins
            ? data.allowedOrigins
                .split('|')
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        );
        setThemeInheritHost(data.themeInheritHost);
        setDisabledMessage(data.disabledMessage ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  function moveField(idx: number, dir: -1 | 1) {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setFields(next);
  }

  function deleteField(idx: number) {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  }

  function addField() {
    setFields((prev) => [
      ...prev,
      {
        name: `custom${prev.length + 1}`,
        label: 'New field',
        type: 'text',
        required: false,
      },
    ]);
  }

  function updateField(idx: number, patch: Partial<FormFieldDef>) {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function addOrigin() {
    const trimmed = originDraft.trim();
    if (!trimmed) return;
    if (allowedOrigins.includes(trimmed)) {
      setOriginDraft('');
      return;
    }
    setAllowedOrigins((prev) => [...prev, trimmed]);
    setOriginDraft('');
  }

  function removeOrigin(origin: string) {
    setAllowedOrigins((prev) => prev.filter((o) => o !== origin));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      const { data } = await apiClient.patch<CaptureFormDetail>(
        `/lead-capture-forms/${id}`,
        {
          title,
          introText: introText.trim() || null,
          thankYouText: thankYouText.trim() || null,
          status,
          fieldConfig: fields,
          embedEnabled,
          allowedOrigins: allowedOrigins.length === 0 ? null : allowedOrigins.join('|'),
          themeInheritHost,
          disabledMessage: disabledMessage.trim() || null,
        },
      );
      setForm(data);
      setToast('Saved');
      setTimeout(() => setToast(null), 2000);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setToast(detail ?? 'Failed to save');
      setTimeout(() => setToast(null), 3000);
    } finally {
      setSaving(false);
    }
  }

  async function copyEmbedSnippet() {
    if (!form) return;
    const origin = window.location.origin;
    const snippet = `<div data-kairos-form="${form.publicToken}"></div>\n<script src="${origin}/widgets/form-widget.js" async></script>`;
    try {
      await navigator.clipboard.writeText(snippet);
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    } catch {
      setToast('Copy failed — select the snippet manually');
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function rotateToken() {
    if (!id) return;
    if (!confirm('Rotate the public token? Existing links will stop working.')) return;
    try {
      const { data } = await apiClient.post<CaptureFormDetail>(
        `/lead-capture-forms/${id}/rotate-token`,
      );
      setForm(data);
      setToast('Token rotated. Update your campaign URLs.');
      setTimeout(() => setToast(null), 4000);
    } catch {
      setToast('Failed to rotate token');
    }
  }

  async function archiveForm() {
    if (!id) return;
    if (!confirm('Archive this form? It will stop accepting submissions.')) return;
    try {
      await apiClient.delete(`/lead-capture-forms/${id}`);
      navigate('/admin/lead-capture-forms');
    } catch {
      setToast('Failed to archive');
    }
  }

  if (loading || !form) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  const publicUrl = `${window.location.origin}/forms/${form.publicToken}`;
  const embedSnippet = `<div data-kairos-form="${form.publicToken}"></div>\n<script src="${window.location.origin}/widgets/form-widget.js" async></script>`;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <Link
        to="/admin/lead-capture-forms"
        className="inline-flex items-center gap-1 text-sm text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Back to forms
      </Link>

      <Card>
        <h1 className="text-2xl font-semibold">{form.title}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          Slug <code>{form.slug}</code> · Status {form.status}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Public URL:</span>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-md bg-muted px-2 py-1 font-mono text-xs hover:bg-muted/80"
          >
            {publicUrl}
          </a>
          <Button size="sm" variant="outline" onClick={rotateToken}>
            <RotateCcw className="mr-1 h-3 w-3" /> Rotate token
          </Button>
        </div>
      </Card>

      <form onSubmit={handleSave} className="space-y-4">
        <Card>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Intro text</label>
              <textarea
                rows={2}
                value={introText}
                onChange={(e) => setIntroText(e.target.value)}
                className="w-full rounded-md border bg-background p-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Thank-you message</label>
              <textarea
                rows={2}
                value={thankYouText}
                onChange={(e) => setThankYouText(e.target.value)}
                className="w-full rounded-md border bg-background p-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Fields
            </h2>
            <Button size="sm" variant="outline" type="button" onClick={addField}>
              <Plus className="mr-1 h-3 w-3" /> Add field
            </Button>
          </div>
          <div className="space-y-3">
            {fields.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No fields. Add at least one before activating.
              </p>
            )}
            {fields.map((field, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 items-center gap-2 rounded-md bg-muted/40 p-3"
              >
                <div className="col-span-1 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => moveField(idx, -1)}
                    disabled={idx === 0}
                    className="rounded p-1 hover:bg-muted disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveField(idx, 1)}
                    disabled={idx === fields.length - 1}
                    className="rounded p-1 hover:bg-muted disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
                <Input
                  className="col-span-3"
                  value={field.name}
                  onChange={(e) => updateField(idx, { name: e.target.value })}
                  placeholder="name"
                />
                <Input
                  className="col-span-3"
                  value={field.label}
                  onChange={(e) => updateField(idx, { label: e.target.value })}
                  placeholder="Label"
                />
                <select
                  className="col-span-2 rounded-md border bg-background px-2 py-2 text-xs"
                  value={field.type}
                  onChange={(e) => updateField(idx, { type: e.target.value })}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
                <label className="col-span-2 flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => updateField(idx, { required: e.target.checked })}
                  />
                  required
                </label>
                <button
                  type="button"
                  onClick={() => deleteField(idx)}
                  className="col-span-1 rounded p-1 text-destructive hover:bg-destructive/10"
                  title="Remove"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Embed
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Drop the snippet on a third-party site. Schema version{' '}
                <code className="rounded bg-muted px-1">v{form.schemaVersion}</code>
                {' '}snapshots automatically when you change fields.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={embedEnabled}
                onChange={(e) => setEmbedEnabled(e.target.checked)}
              />
              Enable embed
            </label>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium">Embed snippet</label>
              <div className="relative">
                <pre className="overflow-x-auto rounded-md border bg-muted/50 p-3 pr-12 text-xs">
                  <code>{embedSnippet}</code>
                </pre>
                <button
                  type="button"
                  onClick={copyEmbedSnippet}
                  className="absolute right-2 top-2 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
                  title="Copy snippet"
                >
                  {snippetCopied ? (
                    <span className="inline-flex items-center gap-1">
                      <Check className="h-3 w-3" /> Copied
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Copy className="h-3 w-3" /> Copy
                    </span>
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                The script auto-mounts every <code>data-kairos-form</code> element on
                the page. Multiple instances per page are supported.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium">Allowed origins</label>
              <p className="text-xs text-muted-foreground">
                Submissions are accepted only from these origins. Use the full origin
                (scheme + host + optional port). Leave empty to block all embed
                submissions.
              </p>
              <div className="flex gap-2">
                <Input
                  value={originDraft}
                  onChange={(e) => setOriginDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOrigin();
                    }
                  }}
                  placeholder="https://www.example.com"
                />
                <Button type="button" variant="outline" onClick={addOrigin}>
                  Add
                </Button>
              </div>
              {allowedOrigins.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {allowedOrigins.map((origin) => (
                    <li
                      key={origin}
                      className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 font-mono text-xs"
                    >
                      {origin}
                      <button
                        type="button"
                        onClick={() => removeOrigin(origin)}
                        className="text-muted-foreground hover:text-destructive"
                        title="Remove origin"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-1">
              <label className="flex items-center gap-2 text-xs font-medium">
                <input
                  type="checkbox"
                  checked={themeInheritHost}
                  onChange={(e) => setThemeInheritHost(e.target.checked)}
                />
                Inherit host page theme
              </label>
              <p className="text-xs text-muted-foreground">
                When enabled the widget reads CSS variables{' '}
                <code>--primary</code>, <code>--font-family</code>,{' '}
                <code>--radius</code> from the host page. Otherwise it uses neutral
                defaults.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium">Disabled message</label>
              <Input
                value={disabledMessage}
                onChange={(e) => setDisabledMessage(e.target.value)}
                placeholder="Shown when the embed is disabled or origin is not allowed"
              />
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={archiveForm}
            className="text-destructive hover:bg-destructive/10"
          >
            Archive form
          </Button>
          <div className="flex items-center gap-3">
            {toast && <span className="text-sm text-muted-foreground">{toast}</span>}
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
